import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Mascot } from '@/components/ui/Mascot';

/**
 * «Продолжить обучение» — оранжевый блок курса. Один компонент на главную и страницу
 * курса (11.09.2026).
 *
 * ⚠️ ДО ЭТОГО ДНЯ БЛОК БЫЛ НАПИСАН ПРЯМО В СТРАНИЦАХ. Компьютер — в
 * `StudentDashboardPage`, телефон — в `MobileHome`, каждый своей разметкой. Владелец
 * попросил тот же блок на страницу курса вместо кнопки «Продолжить: <урок>», и копия
 * разошлась бы с главной при первой же правке. Разметка обоих видов перенесена сюда
 * без изменений, страницы берут её отсюда.
 *
 * Виды — те же, что были на главной:
 * - `desktop` — надзаголовок с обложкой, название, «Урок N из M», полоса с процентом,
 *   две кнопки, маскот `point` справа (на узком экране скрыт);
 * - `mobile` — «📚 Продолжить учиться», название, «Урок N из M», полоса, кнопка,
 *   маскот `start` в углу.
 *
 * `continueTo: null` — продолжать некуда (всё пройдено или всё закрыто): кнопки нет,
 * закрытый урок был бы тупиком. `allLessons` нет — второй кнопки нет, как на главной
 * телефона.
 */
export type ContinueLearningCardProps = {
  variant: 'desktop' | 'mobile';
  title: string;
  coverUrl?: string | null;
  /** Урок, на котором ученик сейчас, — `lessonPosition` в `lib/marathonCards.ts`. */
  lessonNumber: number;
  lessonsTotal: number;
  percent: number;
  continueTo: string | null;
  /** «Все уроки N»: ссылкой (`to`) или кнопкой (`onClick`, например прокрутка к списку). */
  allLessons?: { count: number; to?: string; onClick?: () => void };
};

export function ContinueLearningCard({
  variant,
  title,
  coverUrl,
  lessonNumber,
  lessonsTotal,
  percent,
  continueTo,
  allLessons,
}: ContinueLearningCardProps) {
  const { t } = useTranslation();
  const lessonsLine = t('dashboard.lessonsOf', { done: lessonNumber, total: lessonsTotal });

  if (variant === 'mobile') {
    return (
      <div
        data-continue-card="mobile"
        className="relative overflow-hidden rounded-[32px] bg-gradient-to-br from-primary to-primary-hover px-5 pb-3.5 pt-4 text-on-primary"
      >
        <div className="text-[10px] font-bold uppercase tracking-[0.14em] opacity-90">
          {t('dashboard.continueLearning')}
        </div>
        <div className="mt-2 max-w-[200px] font-display text-[26px] font-bold leading-[1.05] text-white">{title}</div>
        <div className="mt-1 max-w-[190px] text-[12.5px] opacity-95">{lessonsLine}</div>
        <div className="my-3 h-1.5 max-w-[190px] overflow-hidden rounded-full bg-white/35">
          <div className="h-full rounded-full bg-white" style={{ width: `${percent}%` }} />
        </div>
        {/* Кнопки — левее маскота: он занимает правый нижний угол шириной 120px. */}
        <div className="flex max-w-[230px] flex-wrap items-center gap-2">
          {continueTo && (
            <Link
              to={continueTo}
              data-action="continue"
              className="inline-flex w-max items-center gap-2 rounded-full bg-white px-5 py-3 text-[15px] font-bold text-primary-pressed"
            >
              {t('dashboard.continue')}
              <span className="material-symbols-outlined text-[17px]">arrow_forward</span>
            </Link>
          )}
          {allLessons && (
            <AllLessonsAction
              all={allLessons}
              className="inline-flex w-max items-center rounded-full bg-white/20 px-4 py-3 text-[14px] font-semibold text-on-primary"
            />
          )}
        </div>
        {/* Маскот в углу — место из макета (там заглушка «🦊 маскот»). */}
        <Mascot pose="start" className="pointer-events-none absolute -bottom-1.5 -right-2 h-[142px] w-[120px] object-contain" />
      </div>
    );
  }

  return (
    <div data-continue-card="desktop" className="relative min-h-[238px] overflow-hidden rounded-[26px] bg-primary px-7 py-[26px]">
      <div className="max-w-[420px]">
        <div className="flex items-center gap-[9px]">
          {coverUrl && <img src={coverUrl} alt="" className="h-[22px] w-[22px] flex-none rounded-[7px] object-cover" />}
          <span className="text-[9.5px] font-bold uppercase tracking-[0.16em] text-on-primary/85">
            {t('dashboard.continueKicker')}
          </span>
          {/* ⚠️ УРОВНЯ КУРСА ЗДЕСЬ БОЛЬШЕ НЕТ — РЕШЕНИЕ ЗАКАЗЧИКА 07.09.2026. Тут стояло
              «B2–C2» (уровень самого курса), а на телефоне в приветствии — «A1 → A2»
              (уровень ученика). Вещи разные, но человек видит на одном экране два
              несовпадающих уровня и не знает, какому верить. Уровень, которому нельзя
              верить, хуже отсутствия. */}
        </div>
        <h2 className="my-2 mt-3 font-display text-[34px] font-bold leading-[1.05] tracking-[-0.4px] text-on-primary">
          {title}
        </h2>
        <p className="text-[13px] text-on-primary/95">{lessonsLine}</p>
        <div className="my-[18px] mb-5 flex max-w-[330px] items-center gap-[13px]">
          <div className="h-[7px] flex-1 overflow-hidden rounded-full bg-on-primary/30">
            <div className="h-full rounded-full bg-on-primary transition-[width] duration-500" style={{ width: `${percent}%` }} />
          </div>
          <span className="text-[13px] font-bold tabular-nums text-on-primary">{percent}%</span>
        </div>
        <div className="flex flex-wrap items-center gap-[11px]">
          {continueTo && (
            <Link
              to={continueTo}
              data-action="continue"
              className="inline-flex items-center gap-2 rounded-full bg-white px-[22px] py-[13px] text-[14.5px] font-bold text-primary-pressed transition hover:bg-primary-container"
            >
              {t('dashboard.continueBtn')}
              <span className="material-symbols-outlined text-[17px]">arrow_forward</span>
            </Link>
          )}
          {allLessons && (
            <AllLessonsAction
              all={allLessons}
              className="inline-flex items-center rounded-full bg-on-primary/20 px-[22px] py-[13px] text-[13.5px] font-semibold text-on-primary transition hover:bg-on-primary/30"
            />
          )}
        </div>
      </div>
      <Mascot pose="point" className="pointer-events-none absolute bottom-0 right-[18px] hidden h-[214px] w-auto object-contain object-bottom sm:block" />
    </div>
  );
}

/** «Все уроки N» — ссылкой, если есть куда вести, иначе кнопкой. */
function AllLessonsAction({ all, className }: { all: NonNullable<ContinueLearningCardProps['allLessons']>; className: string }) {
  const { t } = useTranslation();
  const label = t('dashboard.allLessonsCount', { count: all.count });
  if (all.to) {
    return (
      <Link to={all.to} data-action="all-lessons" className={className}>
        {label}
      </Link>
    );
  }
  return (
    <button type="button" data-action="all-lessons" onClick={all.onClick} className={className}>
      {label}
    </button>
  );
}
